import { useRef, useState } from "react";
import { Paperclip, Send, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

const ACCEPT = "image/jpeg,image/png,image/webp,image/heic,application/pdf";

export default function Composer({ api, base, disabled, onSent }) {
  const [text, setText] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const pick = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) return toast.error("File must be 10 MB or smaller.");
    setFile(f);
  };

  const send = async () => {
    if (busy || (!text.trim() && !file)) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("text", text.trim());
    if (file) fd.append("file", file);
    try {
      await api.post(`${base}/messages`, fd);
      setText("");
      setFile(null);
      onSent();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not send. Try again.");
    } finally {
      setBusy(false);
    }
  };

  if (disabled) return <div className="border-t border-[#262626] p-3 text-xs text-muted-foreground" data-testid="community-composer-disabled">Posting is turned off for your account.</div>;
  return (
    <div className="border-t border-[#262626] p-3" data-testid="community-composer">
      {file && (
        <div className="mb-2 inline-flex items-center gap-2 text-xs border border-[#262626] px-2 py-1 max-w-full" data-testid="community-composer-file">
          <Paperclip size={12} /> <span className="truncate">{file.name}</span>
          <button onClick={() => setFile(null)} aria-label="Remove attachment" data-testid="community-composer-file-remove"><X size={12} /></button>
        </div>
      )}
      <div className="flex items-end gap-2">
        <button onClick={() => fileRef.current?.click()} aria-label="Attach photo or PDF" data-testid="community-composer-attach"
          className="h-11 w-11 shrink-0 border border-[#262626] hover:border-white flex items-center justify-center transition-colors">
          <Paperclip size={16} />
        </button>
        <input ref={fileRef} type="file" accept={ACCEPT} onChange={pick} className="hidden" data-testid="community-composer-file-input" />
        <textarea value={text} onChange={(e) => setText(e.target.value)} maxLength={2000} rows={1} placeholder="Share a tip, link or question…"
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          data-testid="community-composer-input"
          className="flex-1 min-w-0 min-h-[44px] max-h-40 resize-y px-3 py-2.5 bg-[#141414] border border-[#262626] text-base sm:text-sm focus:outline-none focus:border-white" />
        <button onClick={send} disabled={busy || (!text.trim() && !file)} data-testid="community-composer-send" aria-label="Send"
          className="h-11 px-4 shrink-0 cta-primary font-mono-label text-[11px] inline-flex items-center gap-2 disabled:opacity-50">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} <span className="hidden sm:inline">Send</span>
        </button>
      </div>
    </div>
  );
}
